import React from "react";
import { useSiteMetadata } from "../hooks/use-site-metadata";

export const SEO: React.FC<any> = ({
  title,
  description,
  pathname,
  children,
  keywords,
  image,
  article,
}) => {
  const {
    title: defaultTitle,
    description: defaultDescription,
    siteUrl,
    twitterUsername,
  } = useSiteMetadata();

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${image}`,
    url: `${siteUrl}${pathname || ``}`,
    twitterUsername,
  };

  let keywordsStr;
  if (keywords && typeof keywords === "string") {
    keywordsStr = keywords.trim();
  }
  if (keywords && Array.isArray(keywords)) {
    keywordsStr = keywords.join(", ").trim();
  }

  // Check if we're in production environment
  const isProduction = process.env.APP_ENV === 'prod';
  const currentEnv = process.env.APP_ENV || 'development';

  // In development, also log the environment for debugging
  if (typeof window !== 'undefined' && currentEnv !== 'prod') {
    console.log('🚫 SEO: Non-production environment detected:', currentEnv, '- Adding noindex meta tag');
  }

  return (
    <>
      {/* core */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="canonical" href={seo.url} />

      {/* Robots meta tag - prevent indexing in non-production environments */}
      {!isProduction && (
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
      )}

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:creator" content={seo.twitterUsername} />
      {keywordsStr && <meta name="keywords" content={keywordsStr} />}

      {/* OG */}
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:site_name" content="Propelius Technologies" />

      {/* Article-specific meta tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedAt} />
          <meta property="article:modified_time" content={article.updatedAt || article.publishedAt} />
          <meta property="article:author" content="Propelius Technologies" />
          <meta property="article:section" content={article.category?.data?.attributes?.name || article.category?.Name || article.category || "Technology"} />
          {/* Handle tags from API structure: tags.data array */}
          {article.tags?.data && Array.isArray(article.tags.data) && article.tags.data.map((tag: any, index: number) => (
            <meta key={index} property="article:tag" content={tag.attributes?.name || tag.name || tag} />
          ))}
          {/* Handle keywords as tags if tags are not available */}
          {(!article.tags?.data || article.tags.data.length === 0) && keywordsStr && keywordsStr.split(',').map((keyword: string, index: number) => (
            <meta key={`keyword-${index}`} property="article:tag" content={keyword.trim()} />
          ))}
        </>
      )}

      {/* Structured Data for Articles */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": seo.title,
            "description": seo.description,
            "image": {
              "@type": "ImageObject",
              "url": seo.image,
              "width": 1200,
              "height": 630
            },
            "author": {
              "@type": "Organization",
              "name": "Propelius Technologies",
              "url": siteUrl
            },
            "publisher": {
              "@type": "Organization",
              "name": "Propelius Technologies",
              "url": siteUrl,
              "logo": {
                "@type": "ImageObject",
                "url": `${siteUrl}/propelius-logo.svg`,
                "width": 300,
                "height": 60
              }
            },
            "datePublished": article.publishedAt,
            "dateModified": article.updatedAt || article.publishedAt,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": seo.url
            },
            "keywords": keywordsStr || (article.tags?.data && article.tags.data.length > 0
              ? article.tags.data.map((tag: any) => tag.attributes?.name || tag.name || tag).join(', ')
              : "Technology, Software Development"),
            "articleSection": article.category?.data?.attributes?.name || article.category?.Name || article.category || "Technology",
            "wordCount": article.readingTime ? Math.round(article.readingTime * 200) : undefined,
            "inLanguage": "en-US",
            "copyrightYear": new Date(article.publishedAt).getFullYear(),
            "copyrightHolder": {
              "@type": "Organization",
              "name": "Propelius Technologies"
            },
            // Add tags as keywords if available
            ...(article.tags?.data && article.tags.data.length > 0 && {
              "about": article.tags.data.map((tag: any) => ({
                "@type": "Thing",
                "name": tag.attributes?.name || tag.name || tag
              }))
            })
          })}
        </script>
      )}

      {children}
    </>
  );
};
